import * as React from "react";
import {
  ArrowRight,
  Download,
  FileJson,
  GraduationCap,
  MailWarning,
  RefreshCw,
} from "lucide-react";
import { ListViewCard } from "../ListViewCard";
import { shortDate } from "../DeckCard/logic/formatters";
import * as trophyStyles from "./ListViewUserCertificationCard.style.css";
import type { ListViewCardLayout, ListViewCTAGroup } from "../ListViewCard";
import { ImageLoader } from "../../../ImageLoader";
import { Button } from "../../../Button";
import { getCertificationAppOrigin } from "../../../../../app/lib/auth/app-origins";
import { useI18n } from "../../../../../app/lib/i18n";
import { useCertificationContentDefinitions } from "../../../../../app/lib/i18n/useCertificationContentDefinitions";
import {
  CertificationTrainingProgress,
  type CertificationTrainingMilestone,
} from "./CertificationTrainingProgress";

export type ListViewUserCertificationCardStatus =
  | "not_started"
  | "in_progress"
  | "retake_needed"
  | "complete";

export type { CertificationTrainingMilestone };

export type ListViewUserCertificationMigrationNotice =
  | "full_credit"
  | "partial_reset";

export interface ListViewUserCertificationCardModule {
  moduleId: string;
  moduleTitle: string;
  progressPercent: number;
  completedAt: string | null;
  status: ListViewUserCertificationCardStatus;
  expiresAt: string | null;
  trophyImageUrl?: string | null;
  heroImageUrl?: string | null;
  /** Installer-facing certification number, assigned at module completion. */
  certificateNumber?: string | null;
  /** Ordered training milestones for the stack progress track. */
  trainings?: CertificationTrainingMilestone[];
  /**
   * Optional notice under the progress track (e.g. legacy migration
   * when the live course format no longer matches 1:1).
   */
  migrationNotice?: ListViewUserCertificationMigrationNotice | null;
}

export interface ListViewUserCertificationCardProps {
  layout: ListViewCardLayout;
  certification: ListViewUserCertificationCardModule;
  onClick?: () => void;
  /** When true, show Begin/Next CTA for the signed-in user's own progress. */
  selfView?: boolean;
  /**
   * When true and not `selfView`, show a manager "Send reminder" CTA
   * (requires `onSendReminder`).
   */
  canManage?: boolean;
  /** Opens the org certification reminder modal for this module's next incomplete training. */
  onSendReminder?: (moduleId: string) => void;
  /** Days before expiry to treat as near-expiry (shows Recertify). Default 30. */
  nearExpiryDays?: number;
  onDownloadCertificate?: (moduleId: string) => void;
  generatingCertificate?: boolean;
  isSuperAdmin?: boolean;
  onViewData?: (moduleId: string) => void;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type CertificationVisualState = "complete" | "incomplete" | "expired";

export function getCertificationVisualState(
  cert: Pick<ListViewUserCertificationCardModule, "status" | "expiresAt">,
  now = Date.now(),
): CertificationVisualState {
  if (cert.expiresAt) {
    const expiresMs = new Date(cert.expiresAt).getTime();
    if (!Number.isNaN(expiresMs) && expiresMs <= now) return "expired";
  }
  if (cert.status === "complete") return "complete";
  return "incomplete";
}

export function formatRelativeExpiry(
  expiresAt: string | null | undefined,
  now = Date.now(),
): string | null {
  if (!expiresAt) return null;
  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return null;
  const diffDays = Math.round((expiresMs - now) / MS_PER_DAY);
  if (diffDays >= 0) {
    if (diffDays === 0) return "Expires today";
    if (diffDays === 1) return "Expires in 1 day";
    return `Expires in ${diffDays} days`;
  }
  const ago = Math.abs(diffDays);
  if (ago === 1) return "Expired 1 day ago";
  return `Expired ${ago} days ago`;
}

/** Combined expired notice for the CTA row (replaces footer + separate copy). */
export function formatExpiredRetakeNotice(
  expiresAt: string | null | undefined,
  now = Date.now(),
): string {
  if (!expiresAt) {
    return "This certification has expired and must be retaken.";
  }
  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) {
    return "This certification has expired and must be retaken.";
  }
  const ago = Math.round((now - expiresMs) / MS_PER_DAY);
  if (ago <= 0) {
    return "This certification expired today and must be retaken.";
  }
  if (ago === 1) {
    return "This certification expired 1 day ago and must be retaken.";
  }
  return `This certification expired ${ago} days ago and must be retaken.`;
}

export function isNearOrPastExpiry(
  expiresAt: string | null | undefined,
  nearExpiryDays = 30,
  now = Date.now(),
): boolean {
  if (!expiresAt) return false;
  const expiresMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresMs)) return false;
  const thresholdMs = now + nearExpiryDays * MS_PER_DAY;
  return expiresMs <= thresholdMs;
}

export function getCertificationCourseUrl(moduleId: string): string {
  return `${getCertificationAppOrigin()}/course/${moduleId}`;
}

export function getCertificationLessonUrl(
  moduleId: string,
  trainingId: string,
): string {
  return `${getCertificationAppOrigin()}/course/${moduleId}/lesson/${trainingId}`;
}

/** First incomplete training, or null when all are complete / none exist. */
export function findNextIncompleteTraining(
  trainings: CertificationTrainingMilestone[] | undefined,
): CertificationTrainingMilestone | null {
  if (!trainings?.length) return null;
  return trainings.find((t) => !t.completed) ?? null;
}

/** @deprecated Prefer inline card CTAs via `selfView` on ListViewUserCertificationCard. */
export function getUserCertificationRecertifyCtas(
  cert: ListViewUserCertificationCardModule,
  nearExpiryDays = 30,
): ListViewCTAGroup[] | undefined {
  if (!isNearOrPastExpiry(cert.expiresAt, nearExpiryDays)) return undefined;
  return [
    {
      id: "recertify",
      buttons: [
        {
          id: "recertify",
          label: "Recertify",
          icon: <RefreshCw size={14} />,
          variant: "primary",
          onClick: () => {
            window.open(
              getCertificationCourseUrl(cert.moduleId),
              "_blank",
              "noopener,noreferrer",
            );
          },
        },
      ],
    },
  ];
}

/** @deprecated Prefer inline card CTAs via `selfView` on ListViewUserCertificationCard. */
export function getUserCertificationCardCtas(
  cert: ListViewUserCertificationCardModule,
  opts: {
    selfView?: boolean;
    t: (key: string, values?: Record<string, string | number>) => string;
    nearExpiryDays?: number;
  },
): ListViewCTAGroup[] | undefined {
  return getUserCertificationRecertifyCtas(cert, opts.nearExpiryDays ?? 30);
}

export function ListViewUserCertificationCard({
  layout,
  certification: cert,
  onClick,
  selfView = false,
  canManage = false,
  onSendReminder,
  nearExpiryDays = 30,
  onDownloadCertificate,
  generatingCertificate = false,
  isSuperAdmin = false,
  onViewData,
}: ListViewUserCertificationCardProps) {
  const { t } = useI18n();
  const { resolve } = useCertificationContentDefinitions();
  const visualState = getCertificationVisualState(cert);
  const isExpired = visualState === "expired";
  const relativeExpiry = formatRelativeExpiry(cert.expiresAt);
  const trainings = cert.trainings ?? [];
  const nextTraining =
    selfView && !isExpired ? findNextIncompleteTraining(trainings) : null;
  // Self-view: Recertify when expired or approaching expiry (not Begin/Continue when expired).
  const showRecertify =
    selfView && (isExpired || isNearOrPastExpiry(cert.expiresAt, nearExpiryDays));
  const showSendReminder =
    !selfView && canManage && typeof onSendReminder === "function";
  const showDownloadCertificate =
    cert.status === "complete" && typeof onDownloadCertificate === "function";
  const showViewData = isSuperAdmin && typeof onViewData === "function";

  let footerText: string | undefined;
  if (isExpired) {
    // Combined into the CTA-row notice — avoid overlapping footer.
    footerText = undefined;
  } else if (relativeExpiry) {
    footerText = relativeExpiry;
  } else if (cert.status === "complete" && cert.completedAt) {
    footerText = cert.certificateNumber
      ? `Completed ${shortDate(cert.completedAt)} · Cert No. ${cert.certificateNumber}`
      : `Completed ${shortDate(cert.completedAt)}`;
  }

  const displayModuleTitle =
    resolve(cert.moduleTitle).trim() || cert.moduleTitle;

  let continueLabel: string | null = null;
  if (nextTraining) {
    const hasProgress = trainings.some((tr) => tr.completed);
    const resolvedTitle = resolve(nextTraining.title).trim();
    continueLabel =
      hasProgress && resolvedTitle
        ? t("certification.course.continueCourseNamed", {
            title: resolvedTitle,
          })
        : hasProgress
          ? t("certification.course.continueCourse")
          : t("certification.course.beginCourse");
  }

  const actionButtons =
    continueLabel ||
    showRecertify ||
    showSendReminder ||
    showDownloadCertificate ||
    showViewData ? (
      <>
        {showSendReminder ? (
          <Button
            type="button"
            color="primary"
            variant="solid"
            size="sm"
            className={trophyStyles.badgeActionBtn}
            iconLeft={<MailWarning size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onSendReminder?.(cert.moduleId);
            }}
          >
            Send reminder
          </Button>
        ) : null}
        {showDownloadCertificate ? (
          <Button
            type="button"
            color="primary"
            variant="solid"
            size="sm"
            className={trophyStyles.badgeActionBtn}
            disabled={generatingCertificate}
            iconLeft={<Download size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onDownloadCertificate?.(cert.moduleId);
            }}
          >
            {generatingCertificate ? "Preparing…" : "Download certificate"}
          </Button>
        ) : null}
        {showViewData ? (
          <Button
            type="button"
            color="neutral"
            variant="outline"
            size="sm"
            className={trophyStyles.badgeActionBtn}
            iconLeft={<FileJson size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onViewData?.(cert.moduleId);
            }}
          >
            View data
          </Button>
        ) : null}
        {continueLabel && nextTraining ? (
          <Button
            type="button"
            color="primary"
            variant="solid"
            size="sm"
            className={trophyStyles.badgeActionBtn}
            iconRight={<ArrowRight size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                getCertificationLessonUrl(cert.moduleId, nextTraining.id),
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            {continueLabel}
          </Button>
        ) : null}
        {showRecertify ? (
          <Button
            type="button"
            color="primary"
            variant="solid"
            size="sm"
            className={trophyStyles.badgeActionBtn}
            iconLeft={<RefreshCw size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              window.open(
                getCertificationCourseUrl(cert.moduleId),
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            Recertify
          </Button>
        ) : null}
      </>
    ) : null;

  const migrationNotice =
    cert.migrationNotice === "full_credit" ? (
      <p className={trophyStyles.migrationNoticeCredit}>
        {t("pro.system.certification.claims.previewCarryoverFullCredit")}
      </p>
    ) : cert.migrationNotice === "partial_reset" ? (
      <p className={trophyStyles.migrationNoticeReset}>
        {t("pro.system.certification.claims.previewCarryoverPartialReset")}
      </p>
    ) : null;

  const subtitle = (
    <div className={trophyStyles.cardSubtitleStack}>
      <CertificationTrainingProgress
        progressPercent={cert.progressPercent}
        trainings={trainings}
        trophyImageUrl={cert.trophyImageUrl}
        moduleComplete={visualState === "complete"}
        expired={isExpired}
        completedAt={cert.completedAt}
        expiresAt={cert.expiresAt}
      />
      {migrationNotice}
      {isExpired ? (
        <div className={trophyStyles.cardFooterRow}>
          <p className={trophyStyles.expiredNotice}>
            {formatExpiredRetakeNotice(cert.expiresAt)}
          </p>
          {actionButtons ? (
            <div className={trophyStyles.cardActionsRow}>{actionButtons}</div>
          ) : null}
        </div>
      ) : actionButtons ? (
        <div
          className={[
            trophyStyles.cardActionsRow,
            trophyStyles.cardActionsRowFull,
          ].join(" ")}
        >
          {actionButtons}
        </div>
      ) : null}
    </div>
  );

  const hero = cert.heroImageUrl ? (
    <ImageLoader
      src={cert.heroImageUrl}
      alt=""
      objectFit="cover"
      className={trophyStyles.heroCover}
    />
  ) : (
    <GraduationCap
      size={layout === "deck" ? 40 : 32}
      style={{ opacity: 0.55 }}
    />
  );

  return (
    <ListViewCard
      id={cert.moduleId}
      layout={layout}
      imageFormat="cover"
      image={hero}
      title={displayModuleTitle}
      subtitle={subtitle}
      titleEnd={layout === "stack" ? footerText : undefined}
      footerText={layout === "stack" ? undefined : footerText}
      onClick={onClick}
    />
  );
}
